import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import EditButton from "@accredo/fe-shared/theme/components/EditButton.jsx";
import {
  ContentDialog,
  ContentDialogInit,
} from "../MDDForm/components/ContentDialog.jsx";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useSnackbar } from "notistack";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { useAtom } from "jotai";
import { backendDataAtom } from "../../atoms/backendDataAtom.js";
import { useClaimCommunicationActions } from "../../hooks/useClaimCommunicationActions.jsx";
import NoteForm from "./NoteForm.jsx";
import NoteDetailView from "./NoteDetailView.jsx";

function ClaimNoteModal({ open, onClose, kommunikation_id, schaden_id }) {
  const { user } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [note, setNote] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const { openCD, setOpenCD, tca, setTca, CDActions, setCDActions } =
    ContentDialogInit();

  const [backendData] = useAtom(backendDataAtom);
  const {
    createCommunication,
    updateCommunication,
    updateCommunicationReminder,
  } = useClaimCommunicationActions();

  const schaden = backendData?.data || {};

  // Fetch kommunikation data when modal opens
  useEffect(() => {
    const fetchKommunikation = async () => {
      try {
        await backendGet(
          `data/kommunikations/${schaden_id}/${kommunikation_id}`,
          {
            options: {
              params: {
                // filter:  {id: kommunikation_id},
                include: ["createdBy"],
              },
            },
            setResult: (res) => {
              const data = res || {};
              setNote(data);
            },
            setIsLoading,
          }
        );
      } catch (error) {
        enqueueSnackbar("Fehler beim Laden der Kommunikation", {
          variant: "error",
        });
      }
    };

    if (open && kommunikation_id) {
      fetchKommunikation();
    } else {
      setNote(null);
    }
    // eslint-disable-next-line
  }, [open, kommunikation_id]);

  useEffect(() => {
    if (open && kommunikation_id) {
      setOpenCD(true);
      setEditMode(false);
      setCDActions(null);
      setTca({
        title: "Notiz-Details",
        maxWidth: "md",
        content: isLoading ? <div>Daten werden geladen...</div> : null,
        actions: null,
      });
    } else if (open && !kommunikation_id) {
      setNote(null);
      setOpenCD(true);
      setEditMode(true);
      setCDActions(null);
      showNoteAdd();
    } else {
      setOpenCD(false);
      setEditMode(false);
      setCDActions(null);
    }
    // eslint-disable-next-line
  }, [open, kommunikation_id, isLoading]);

  useEffect(() => {
    if (open && note) {
      processNoteToView(note);
    }
    // eslint-disable-next-line
  }, [open, note, editMode]);

  const showNoteAdd = () => {
    const defaultValues = {
      schaden_id,
      type: "note",
      intern: true,
      betreff: "",
      beschreibung: "",
    };

    setOpenCD(true);
    setTca({
      title: "Neue Notiz",
      maxWidth: "md",
      content: (
        <NoteForm
          defaultValues={defaultValues}
          onSubmit={async (data) => {
            try {
              const newComm = await createCommunication(
                { ...data, type: "note" },
                {
                  onSuccess: () => {
                    enqueueSnackbar("Notiz erstellt", {
                      variant: "success",
                    });
                  },
                }
              );

              await updateCommunicationReminder(newComm.id, data);
              onClose();

              return true;
            } catch (e) {
              console.error(e);
              return false;
            }
          }}
          onCancel={onClose}
          disabled={false}
        />
      ),
      actions: null,
    });
  };

  const processNoteToView = (data) => {
    const isOwner = data?.created_by?.id === user.id;
    const isAccredo =
      user.role_names.includes("sysadmin") ||
      user.role_names.includes("accredo");
    const canEdit = isOwner || isAccredo;

    const defaultValues = {
      ...data,
      schaden_id,
      type: "note",
      intern: !!data?.intern,
      betreff: data?.betreff ?? "",
      beschreibung: data?.beschreibung ?? "",
    };

    setCDActions({
      top:
        canEdit && !editMode ? (
          <EditButton
            onClick={() => {
              setEditMode(true);
              setCDActions({ top: null });
            }}
          />
        ) : null,
    });

    setTca({
      title: "Notiz Details",
      maxWidth: "md",
      content: editMode ? (
        <NoteForm
          key={`${kommunikation_id}-edit`}
          defaultValues={defaultValues}
          onSubmit={async (formData) => {
            try {
              await updateCommunication(
                { ...formData, type: "note" },
                {
                  onSuccess: () => {
                    enqueueSnackbar("Notiz gespeichert", {
                      variant: "success",
                    });
                  },
                }
              );

              await updateCommunicationReminder(kommunikation_id, formData);

              onClose();
              return true;
            } catch (e) {
              return false;
            }
          }}
          onCancel={onClose}
          disabled={false}
        />
      ) : (
        <NoteDetailView
          key={`${kommunikation_id}-detail`}
          note={data}
          onClose={onClose}
        />
      ),
      actions: null,
    });
  };

  return (
    <ContentDialog
      openCD={openCD}
      setOpenCD={setOpenCD}
      tca={tca}
      CDActions={CDActions}
      onClose={onClose}
    />
  );
}

ClaimNoteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  kommunikation_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  schaden_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ClaimNoteModal;
